/**
 * CarSpriteEngine — pure heading → sprite-frame resolution for the
 * Experimental Premium Live Map Layer (PLA-262 / PLA-265).
 *
 * ## Heading & frame convention (MUST match the car sprite sheet)
 *
 * - Headings are compass degrees: 0° = NORTH (up), increasing CLOCKWISE.
 *   90° = east, 180° = south, 270° = west.
 * - Frame 0 of the sprite sheet shows the car pointing NORTH.
 * - Frames advance CLOCKWISE: with the default 64 frames each frame spans
 *   360 / 64 = 5.625°, so frame 16 = east, frame 32 = south, frame 48 = west.
 * - A heading is quantized to the NEAREST frame center, i.e. frame k covers
 *   headings in [k·step − step/2, k·step + step/2).
 *
 * This module is intentionally pure TypeScript: no DOM, no google.maps, no
 * React. `resolveFrame` is a pure function over (state, input) → (state,
 * output); callers own the state and feed back the returned state on the
 * next tick.
 */

/** Tunable behavior for the sprite engine. */
export interface CarSpriteConfig {
  /** Number of frames in the sprite sheet. Frame 0 = north, clockwise. */
  frameCount: number
  /**
   * Maximum angular velocity (degrees per second) applied to the displayed
   * heading. Large heading jumps are animated over several resolve calls
   * instead of snapping.
   */
  maxAngularVelocityDegPerSec: number
  /**
   * Speed gate (meters per second). When the input speed is undefined or
   * below this threshold the last good heading is HELD — GPS heading is
   * noise when (nearly) stationary.
   */
  minSpeedMps: number
  /**
   * When true, `transition` is always "none" (reduced-motion mode); frame
   * changes should be applied without a crossfade.
   */
  disableCrossfade: boolean
  /**
   * Minimum time (ms) between emitted "crossfade" transitions. Frame changes
   * arriving faster than this are applied as plain swaps ("none") so a new
   * fade never interrupts one still in flight — interrupted fades leave both
   * sprite layers semi-transparent, blending two rotation frames.
   */
  minCrossfadeIntervalMs: number
}

export const DEFAULT_CAR_SPRITE_CONFIG: CarSpriteConfig = {
  frameCount: 64,
  maxAngularVelocityDegPerSec: 180,
  minSpeedMps: 1,
  disableCrossfade: false,
  minCrossfadeIntervalMs: 120,
}

/** Engine state. Treat as opaque; create via `createSpriteState`. */
export interface CarSpriteState {
  /** Smoothed heading currently displayed, normalized to [0, 360). */
  displayHeading: number
  /** Frame index emitted by the previous resolve call. */
  lastFrameIndex: number
  /** Timestamp of the previous resolve call, or null before the first one. */
  lastTimestampMs: number | null
  /** Whether a valid (speed-gated) heading has ever been accepted. */
  hasHeading: boolean
  /** Timestamp of the last frame-index change, or null before the first one. */
  lastFrameChangeMs: number | null
}

/** A single tracking sample fed into `resolveFrame`. */
export interface CarSpriteInput {
  /** Compass heading in degrees (0 = north, clockwise). Optional/noisy. */
  heading?: number
  /** Ground speed in meters per second. Optional. */
  speed?: number
  /** Monotonic-ish timestamp of this sample in milliseconds. */
  timestampMs: number
}

export type CarSpriteTransition = "none" | "crossfade"

export interface CarSpriteOutput {
  /** Sprite frame to display, in [0, frameCount). */
  frameIndex: number
  /** Smoothed heading backing that frame, in [0, 360). */
  displayHeading: number
  /**
   * "crossfade" when the frame index changed since the previous resolve AND
   * at least `minCrossfadeIntervalMs` passed since the last frame change
   * (consumer decides the duration); "none" otherwise — including rapid
   * frame churn — or when crossfades are disabled via config.
   */
  transition: CarSpriteTransition
}

export interface CarSpriteResolution {
  state: CarSpriteState
  output: CarSpriteOutput
}

/** Creates the initial engine state (car shown facing north, frame 0). */
export function createSpriteState(initialHeading: number = 0): CarSpriteState {
  const heading = normalizeHeading(initialHeading)
  return {
    displayHeading: heading,
    lastFrameIndex: headingToFrame(heading, DEFAULT_CAR_SPRITE_CONFIG.frameCount),
    lastTimestampMs: null,
    hasHeading: false,
    lastFrameChangeMs: null,
  }
}

/** Wraps any finite degree value into [0, 360). */
export function normalizeHeading(degrees: number): number {
  const wrapped = degrees % 360
  return wrapped < 0 ? wrapped + 360 : wrapped
}

/**
 * Signed shortest angular delta from `fromDeg` to `toDeg`, in (-180, 180].
 * Positive = clockwise. E.g. 350° → 10° yields +20 (through north), never
 * −340 the long way around.
 */
export function shortestAngleDelta(fromDeg: number, toDeg: number): number {
  const raw = normalizeHeading(toDeg) - normalizeHeading(fromDeg)
  if (raw > 180) return raw - 360
  if (raw <= -180) return raw + 360
  return raw
}

/**
 * Quantizes a heading to the nearest sprite frame.
 * Frame 0 = 0° (north); frames advance clockwise.
 */
export function headingToFrame(headingDeg: number, frameCount: number): number {
  const degreesPerFrame = 360 / frameCount
  return Math.round(normalizeHeading(headingDeg) / degreesPerFrame) % frameCount
}

/**
 * Resolves the sprite frame for a tracking sample.
 *
 * Pure: returns a NEW state plus the render output; never mutates inputs.
 *
 * Behavior:
 * - Speed gate: heading updates are ignored while `speed` is undefined or
 *   below `minSpeedMps`; the last good heading is held.
 * - Smoothing: the displayed heading rotates toward the target along the
 *   SHORTEST angular path, capped at `maxAngularVelocityDegPerSec`, so a
 *   large jump animates across consecutive calls instead of snapping.
 * - First accepted heading snaps immediately (no prior baseline to animate
 *   from).
 * - `transition` is "crossfade" when the emitted frame index differs from the
 *   previous call AND it is either the first frame change or at least
 *   `minCrossfadeIntervalMs` has elapsed since the last one — frame churn
 *   faster than the fade duration gets plain swaps ("none") so fades never
 *   interrupt each other. `disableCrossfade` forces "none" throughout.
 */
export function resolveFrame(
  state: CarSpriteState,
  input: CarSpriteInput,
  config: Partial<CarSpriteConfig> = {},
): CarSpriteResolution {
  const cfg: CarSpriteConfig = { ...DEFAULT_CAR_SPRITE_CONFIG, ...config }

  const headingIsUsable =
    input.heading !== undefined &&
    Number.isFinite(input.heading) &&
    input.speed !== undefined &&
    Number.isFinite(input.speed) &&
    input.speed >= cfg.minSpeedMps

  let displayHeading = state.displayHeading

  if (headingIsUsable) {
    const target = normalizeHeading(input.heading as number)
    if (!state.hasHeading || state.lastTimestampMs === null) {
      // First good fix: snap — there is no visual baseline to rotate from.
      displayHeading = target
    } else {
      const dtSeconds = Math.max(0, (input.timestampMs - state.lastTimestampMs) / 1000)
      const maxStep = cfg.maxAngularVelocityDegPerSec * dtSeconds
      const delta = shortestAngleDelta(state.displayHeading, target)
      const step = Math.max(-maxStep, Math.min(maxStep, delta))
      displayHeading = normalizeHeading(state.displayHeading + step)
    }
  }
  // else: speed gate (or missing heading) — hold the last good heading.

  const frameIndex = headingToFrame(displayHeading, cfg.frameCount)
  const frameChanged = frameIndex !== state.lastFrameIndex
  const crossfadeAllowed =
    state.lastFrameChangeMs === null ||
    input.timestampMs - state.lastFrameChangeMs >= cfg.minCrossfadeIntervalMs
  const transition: CarSpriteTransition =
    !cfg.disableCrossfade && frameChanged && crossfadeAllowed ? "crossfade" : "none"

  return {
    state: {
      displayHeading,
      lastFrameIndex: frameIndex,
      lastTimestampMs: input.timestampMs,
      hasHeading: state.hasHeading || headingIsUsable,
      lastFrameChangeMs: frameChanged ? input.timestampMs : state.lastFrameChangeMs,
    },
    output: { frameIndex, displayHeading, transition },
  }
}
